import React, { useState, useEffect } from 'react';
import { useApiService } from '../../services/api';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  DollarSign, 
  FileText, 
  CreditCard,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import DealForm from './DealForm';
import DealView from './DealView';
import exportDeliveryOrderPDF from './PrintDO';
import './Deals.css';

const Deals = () => {
  const apiService = useApiService();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDeal, setViewingDeal] = useState(null);
  const [showroomDetails, setShowroomDetails] = useState(null);
  const [exportingPurchaseDO, setExportingPurchaseDO] = useState({});
  const [exportingSellDO, setExportingSellDO] = useState({});

  useEffect(() => {
    loadDeals();
    loadStats();
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadShowroomDetails();
  }, []);

  const loadShowroomDetails = async () => {
    try {
      const response = await apiService.getUserShowroom();
      setShowroomDetails(response || {});
    } catch (error) {
      console.error('Error loading showroom details:', error);
    }
  };

  const loadDeals = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllDeals({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });
      setDeals(response.deals || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error loading deals:', error);
      alert('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.getDealStats();
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleDeleteDeal = async (id) => {
    // Find the deal to check its status
    const deal = deals.find(d => d.id === id);
    if (deal && deal.status === 'deleted') {
      alert('This deal is already deleted');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    try {
      await apiService.deleteDeal(id);
      await loadDeals();
    } catch (err) {
      alert('Failed to delete deal. Please try again.');
    }
  };

  const handleViewDeal = (deal) => {
    setViewingDeal(deal);
    setShowViewModal(true);
  };

  const handleEditDeal = (deal) => {
    // Prevent editing deleted deals
    if (deal.status === 'deleted') {
      alert('Cannot edit a deleted deal');
      return;
    }
    setEditingDeal(deal);
    setShowEditModal(true);
  };

  const handleExportPurchaseDO = async (dealId) => {
    setExportingPurchaseDO(prev => ({ ...prev, [dealId]: true }));
    try {
      const data = await apiService.generatePurchaseDO(dealId);
      await exportDeliveryOrderPDF(data, 'purchase', showroomDetails, (loading) => {
        setExportingPurchaseDO(prev => ({ ...prev, [dealId]: loading }));
      });
    } catch (error) {
      console.error('Error exporting purchase DO:', error);
      alert('Failed to export purchase DO');
      setExportingPurchaseDO(prev => ({ ...prev, [dealId]: false }));
    }
  };

  const handleExportSellDO = async (dealId) => {
    setExportingSellDO(prev => ({ ...prev, [dealId]: true }));
    try {
      const data = await apiService.generateSellDO(dealId);
      await exportDeliveryOrderPDF(data, 'sell', showroomDetails, (loading) => {
        setExportingSellDO(prev => ({ ...prev, [dealId]: loading }));
      });
    } catch (error) {
      console.error('Error exporting sell DO:', error);
      alert('Failed to export sell DO');
      setExportingSellDO(prev => ({ ...prev, [dealId]: false }));
    }
  };

  const handleSuccess = () => {
    loadDeals();
    loadStats();
  };

  const getCarDisplayName = (car) => {
    if (!car) return 'N/A';
    const reg = car.registrationNumber || '';
    const chassis = car.chassisNumber || '';
    const engine = car.engineNumber || '';
    return `${car.name} ${car.vehicleModel} ${reg ? `(${reg})` : chassis ? `(${chassis})` : engine ? `(${engine})` : ''}`.trim();
  };

  const getCustomerDisplayName = (customer) => {
    if (!customer) return 'N/A';
    const identifier = customer.cnic || customer.phoneNumber || 'N/A';
    return `${customer.fullName} (${identifier})`;
  };

  // Helper to safely convert string to number for display
  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }
    return typeof value === 'number' ? value : 0;
  };

  const formatCurrency = (amount) => {
    // Handle both string and number inputs
    const numAmount = toNumber(amount);
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-PK');
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Deals Management</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage and monitor your vehicle deals</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}>
            <Search size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add New Deal</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CreditCard size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Deals</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{stats.totalDeals || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#16a34a' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Purchase Value</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(stats.totalPurchaseValue || 0)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#dc2626' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Sale Value</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(stats.totalSaleValue || 0)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Profit</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(stats.totalProfit || 0)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search deals by ID or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '16px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {deals.length} deals
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Deals Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading deals...</div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Deal ID</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Car</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Purchased From</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Sold To</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Purchase Value</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Sale Value</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Updated By</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr key={deal.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{deal.dealId}</td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{getCarDisplayName(deal.car)}</td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{getCustomerDisplayName(deal.purchaseInfo?.customer)}</td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{getCustomerDisplayName(deal.sellInfo?.customer)}</td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{formatCurrency(deal.purchaseInfo?.value)}</td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{formatCurrency(deal.sellInfo?.value)}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem', 
                      fontWeight: '600', 
                      textTransform: 'uppercase',
                      background: deal.sellInfo ? '#d1fae5' : '#fef3c7',
                      color: deal.sellInfo ? '#065f46' : '#92400e'
                    }}>
                      {deal.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{deal.updatedByName || deal.updatedBy || 'Not set'}</td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => handleViewDeal(deal)}
                        style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#16a34a' }}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleEditDeal(deal)}
                        disabled={deal.status === 'deleted'}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          padding: '0.5rem', 
                          borderRadius: '6px', 
                          cursor: deal.status === 'deleted' ? 'not-allowed' : 'pointer', 
                          color: deal.status === 'deleted' ? '#9ca3af' : '#6366f1',
                          opacity: deal.status === 'deleted' ? 0.5 : 1
                        }}
                        title={deal.status === 'deleted' ? 'Cannot edit deleted deal' : 'Edit'}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteDeal(deal.id)}
                        disabled={deal.status === 'deleted'}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          padding: '0.5rem', 
                          borderRadius: '6px', 
                          cursor: deal.status === 'deleted' ? 'not-allowed' : 'pointer', 
                          color: deal.status === 'deleted' ? '#9ca3af' : '#dc2626',
                          opacity: deal.status === 'deleted' ? 0.5 : 1
                        }}
                        title={deal.status === 'deleted' ? 'Deal already deleted' : 'Delete'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{ 
              background: 'white', 
              border: '1px solid #d1d5db', 
              padding: '0.5rem 1rem', 
              borderRadius: '6px', 
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            Previous
          </button>
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{ 
              background: 'white', 
              border: '1px solid #d1d5db', 
              padding: '0.5rem 1rem', 
              borderRadius: '6px', 
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      <DealForm
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleSuccess}
        mode="add"
      />

      <DealForm
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleSuccess}
        deal={editingDeal}
        mode="edit"
      />

      <DealView
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        deal={viewingDeal}
        onExportPurchaseDO={handleExportPurchaseDO}
        onExportSellDO={handleExportSellDO}
        showroomDetails={showroomDetails}
      />
    </div>
  );
};

export default Deals; 